// Allowance entitlements — what a month's attendance says you're owed.
//
// Deliberately read-only about money: nothing here pays anyone. Entitlements are
// derived from the payroll day-breakdown, and turning one into money is a claim
// submitted through Expenses, which already has approval, receipts and the
// accounting push. Showing a total here and paying it somewhere else would be
// two sources of truth for the same rupee.

import { useCallback, useEffect, useState } from 'react';
import {
  HandCoins, Info, AlertTriangle, Loader2, Calculator, Save,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  previewAllowances,
  saveCommuteDeclaration,
  type AllowancePreview,
} from '../api/policyMoney';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Indian financial year: April to March. A date in Jan–Mar belongs to the FY
// that began the previous April.
function financialYearOf(d: Date): string {
  const start = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export default function Allowances() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<AllowancePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [commute, setCommute] = useState('');
  const [savingDecl, setSavingDecl] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const financialYear = financialYearOf(new Date(year, month - 1, 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewAllowances(year, month);
      setData(res);
      setCommute(res.declared_commute != null ? String(res.declared_commute) : '');
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.status === 403
          ? "You don't have access to allowances."
          : err?.response?.data?.detail || 'Could not load allowances.',
      );
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const saveDeclaration = async () => {
    setSavingDecl(true);
    try {
      await saveCommuteDeclaration({
        financial_year: financialYear,
        usual_commute_amount: Number(commute || 0),
      });
      setToast(`Saved for ${financialYear}.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not save your declaration.');
    } finally {
      setSavingDecl(false);
    }
  };

  const entitlements = data?.entitlements ?? [];
  // Admin / head-office accounts have no employee row, so there is nothing to
  // declare a commute against. Hiding the card is kinder than leaving a Save
  // button that can only ever fail.
  const hasProfile = data?.has_profile !== false;

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <HandCoins className="h-6 w-6 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold">Allowances</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              What this month's attendance entitles you to claim.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-900 rounded-xl p-3">
          <Info size={16} className="mt-0.5 shrink-0" />
          <p>
            These are entitlements, not payments. To be paid, file them as a
            claim in <Link to="/expenses" className="font-semibold underline">Expenses</Link>,
            where they go through the usual approval.
          </p>
        </div>

        {toast && (
          <div role="status" className="flex items-start justify-between gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3">
            <p>{toast}</p>
            <button onClick={() => setToast(null)} aria-label="Dismiss" className="text-emerald-700">×</button>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Month" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select aria-label="Year" className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm bg-white"
            value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear() - 1, now.getFullYear()].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </p>
        ) : data?.note ? (
          // No payroll breakdown exists yet — entitlements are derived from it,
          // so say that rather than showing a misleading zero.
          <div className="flex items-start gap-2 text-sm bg-white border border-[#e8e3d8] rounded-xl p-4 text-gray-600">
            <Calculator size={16} className="mt-0.5 shrink-0 text-gray-400" />
            <p>{data.note}</p>
          </div>
        ) : entitlements.length === 0 ? (
          <div className="flex items-start gap-2 text-sm bg-white border border-[#e8e3d8] rounded-xl p-4 text-gray-600">
            <Info size={16} className="mt-0.5 shrink-0 text-gray-400" />
            <p>
              Nothing for {MONTHS[month - 1]}. Allowances appear here when the
              month's attendance meets a rule your organisation has configured —
              working late, or working a week-off.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#e8e3d8] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-[#f0ece2]">
                    <th className="py-2 px-3 font-medium">Date</th>
                    <th className="py-2 px-3 font-medium">Allowance</th>
                    <th className="py-2 px-3 font-medium">Why</th>
                    <th className="py-2 px-3 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {entitlements.map((e, i) => (
                    <tr key={`${e.date}-${e.rule_key}-${i}`} className="border-b border-[#f7f4ec] last:border-0">
                      <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{e.date}</td>
                      <td className="py-2 px-3 text-gray-900">{e.label}</td>
                      <td className="py-2 px-3 text-gray-500 text-xs">{e.reason}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900 whitespace-nowrap">
                        {money(e.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-[#e8e3d8] bg-[#faf8f3]">
                    <td colSpan={3} className="py-2 px-3 font-medium text-gray-700">Total</td>
                    <td className="py-2 px-3 text-right font-semibold text-gray-900">
                      {money(data?.total ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {hasProfile && (
        <div className="bg-white border border-[#e8e3d8] rounded-2xl p-4 space-y-3">
          <div>
            <h2 className="font-semibold text-gray-900">Your usual commute cost</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Declared once per financial year ({financialYear}). Conveyance is
              reimbursed as the difference between what a trip actually cost and
              what your normal commute would have cost anyway — without this,
              that can't be worked out.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-600">Home ↔ office (₹)</span>
              <input
                type="number" min={0}
                className="border border-[#e8e3d8] rounded-lg px-2 py-1.5 text-sm w-40 mt-1"
                value={commute}
                onChange={(e) => setCommute(e.target.value)}
                placeholder="0"
              />
            </label>
            <button
              onClick={saveDeclaration}
              disabled={savingDecl || commute === ''}
              className="bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <Save size={14} /> {savingDecl ? 'Saving…' : 'Save'}
            </button>
          </div>
          {data?.missing_declaration && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Not declared yet — conveyance allowances stay hidden until you do.
            </p>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
